import { iconSizes, r, s } from '@/theme/tokens';
import {
  Box,
  Skeleton,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Typography,
} from '@/ui/primitives';
import { KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import { buildTableDividerPseudoSx } from '../tableSystem/tableDividerSx';
import {
  formatHistoryHeaderDay,
  HEADER_SUBTEXT_SX,
  HEADER_SEPARATOR_SX,
  HEADER_TEXT_SX,
  HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX,
  resolveHistoryDayHeaderFallbackInterval,
  shouldShowDayHeaderLabel,
} from './EntityDailyHistoryPanel.constants';
import type { DailyHistoryCell } from './EntityDailyHistoryPanel.types';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';

type EntityDailyHistoryPanelSkeletonProps = {
  dayCount: number;
  days?: DailyHistoryCell[];
  entityLabel?: string;
  entityHeaderContent: ReactNode;
  sortDirection: 'asc' | 'desc';
  rowCount: number;
  nameColumnWidth: string;
  rowHeight: number;
  blockSize: number;
  headerHeight: number;
  visibleDayHeaderIndices?: number[];
  dayHeaderGridRef: RefObject<HTMLDivElement | null>;
  nameColumnHeaderCellRef: RefObject<HTMLTableCellElement | null>;
  onNameColumnResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onNameColumnResizeKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
};

const HISTORY_SKELETON_RADIUS = '999px';
const HISTORY_ROW_NAME_SKELETON_HEIGHT = 16;
const HISTORY_STATUS_BLOCK_SKELETON_HEIGHT = 18;
const ROW_NAME_WIDTHS = ['72%', '64%', '68%', '70%', '62%'] as const;

type VisibleDayHeaderLabel = {
  day: DailyHistoryCell;
  index: number;
};

type HistorySkeletonColumnProps = {
  nameColumnWidth: string;
};

type HistorySkeletonHeaderSectionProps = HistorySkeletonColumnProps & {
  dayCount: number;
  entityLabel: string;
  entityHeaderContent: ReactNode;
  sortDirection: 'asc' | 'desc';
  headerHeight: number;
  dayHeaderGridRef: RefObject<HTMLDivElement | null>;
  nameColumnHeaderCellRef: RefObject<HTMLTableCellElement | null>;
  onNameColumnResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onNameColumnResizeKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
  visibleDayHeaderLabelsByIndex: Map<number, DailyHistoryCell>;
};

type HistorySkeletonBodySectionProps = HistorySkeletonColumnProps & {
  blockSize: number;
  dayCount: number;
  rowCount: number;
  rowHeight: number;
};

const buildVisibleDayHeaderLabels = (
  days: DailyHistoryCell[],
  visibleDayHeaderIndices?: number[],
): VisibleDayHeaderLabel[] => {
  if (days.length === 0 || !visibleDayHeaderIndices) {
    return [];
  }

  return visibleDayHeaderIndices.flatMap((index) => {
    if (index < 0 || index >= days.length) {
      return [];
    }

    const day = days[index];
    return [{ day, index }];
  });
};

const EntityDailyHistorySkeletonColumns = ({
  nameColumnWidth,
}: HistorySkeletonColumnProps): ReactElement => (
  <colgroup>
    <col width={nameColumnWidth} />
    <col width="100%" />
  </colgroup>
);

const historySkeletonTableSx = (nameColumnWidth: string): SxProps<Theme> => ({
  tableLayout: 'fixed',
  width: '100%',
  backgroundColor: 'background.paper',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
    borderTop: 'none',
    pt: s('sm'),
  },
  '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
    pb: s('sm'),
  },
  '& .MuiTableCell-root, & .MuiTableRow-root, & .MuiTableHead-root, & .MuiTableBody-root': {
    backgroundColor: 'background.paper',
  },
  '& col:first-of-type': {
    width: nameColumnWidth,
  },
  '& col:last-of-type': {
    width: '100%',
  },
});

const historySkeletonNameCellSx = (nameColumnWidth: string): SxProps<Theme> => [
  {
    backgroundColor: 'background.paper',
    width: nameColumnWidth,
    minWidth: nameColumnWidth,
    maxWidth: nameColumnWidth,
    px: s('xl'),
    pt: s('md'),
    pb: s('none'),
    verticalAlign: 'top',
    borderBottom: 'none',
    position: 'relative',
  },
  HEADER_SEPARATOR_SX,
];

const historySkeletonBodyNameCellSx = (nameColumnWidth: string): SxProps<Theme> => [
  {
    backgroundColor: 'background.paper',
    width: nameColumnWidth,
    minWidth: nameColumnWidth,
    maxWidth: nameColumnWidth,
    px: s('xl'),
    py: s('none'),
    verticalAlign: 'middle',
    borderBottom: 'none',
  },
];

const EntityDailyHistorySkeletonHeaderSection = ({
  dayCount,
  entityLabel,
  entityHeaderContent,
  sortDirection,
  headerHeight,
  dayHeaderGridRef,
  nameColumnWidth,
  nameColumnHeaderCellRef,
  onNameColumnResizeStart,
  onNameColumnResizeKeyDown,
  visibleDayHeaderLabelsByIndex,
}: HistorySkeletonHeaderSectionProps): ReactElement => (
  <TableHead>
    <TableRow
      sx={{
        backgroundColor: 'background.paper',
        height: `${headerHeight}px`,
        position: 'relative',
        '&::after': buildTableDividerPseudoSx('bottom'),
      }}
    >
      <TableCell ref={nameColumnHeaderCellRef} sx={historySkeletonNameCellSx(nameColumnWidth)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: s('sm'),
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'inline-flex',
              gap: s('sm'),
              minWidth: 0,
            }}
          >
            <Typography sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>{entityLabel}</Typography>
            <KeyboardArrowDownIcon
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                fontSize: iconSizes.lg,
                transform: sortDirection === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
          {entityHeaderContent}
        </Box>
        <Box
          component="button"
          type="button"
          aria-label={`Resize ${entityLabel} column`}
          onPointerDown={onNameColumnResizeStart}
          onKeyDown={onNameColumnResizeKeyDown}
          sx={{
            position: 'absolute',
            top: 0,
            right: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX / -2)}px`,
            width: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX)}px`,
            height: '100%',
            p: s('none'),
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'col-resize',
            zIndex: 1,
            touchAction: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: s('sm'),
              bottom: s('sm'),
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              borderRadius: '999px',
              borderLeft: '2px solid',
              borderColor: 'divider',
              opacity: 0,
              transition: (muiTheme) =>
                muiTheme.transitions.create('opacity', {
                  duration: muiTheme.transitions.duration.shortest,
                }),
            },
            '&:hover::after, &:focus-visible::after, &:active::after': {
              opacity: 1,
            },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          backgroundColor: 'background.paper',
          px: s('xl'),
          pt: s('md'),
          pb: s('none'),
          verticalAlign: 'top',
          borderBottom: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('sm'), minWidth: 0 }}>
          <Typography sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>Status</Typography>
          <EntityDailyHistorySkeletonDayHeaderSection
            dayCount={dayCount}
            dayHeaderGridRef={dayHeaderGridRef}
            visibleDayHeaderLabelsByIndex={visibleDayHeaderLabelsByIndex}
          />
        </Box>
      </TableCell>
    </TableRow>
  </TableHead>
);

const EntityDailyHistorySkeletonDayHeaderSection = ({
  dayCount,
  dayHeaderGridRef,
  visibleDayHeaderLabelsByIndex,
}: {
  dayCount: number;
  dayHeaderGridRef: RefObject<HTMLDivElement | null>;
  visibleDayHeaderLabelsByIndex: Map<number, DailyHistoryCell>;
}): ReactElement => {
  const headerInterval = resolveHistoryDayHeaderFallbackInterval(dayCount);
  const hasVisibleLabels = visibleDayHeaderLabelsByIndex.size > 0;

  return (
    <Box
      ref={dayHeaderGridRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
        columnGap: s('md'),
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        height: '16px',
        pb: s('none'),
      }}
    >
      {Array.from({ length: dayCount }, (_unused, index) => {
        const visibleDay = visibleDayHeaderLabelsByIndex.get(index);
        const showFallbackSpacer =
          !hasVisibleLabels && shouldShowDayHeaderLabel(index, dayCount - 1, headerInterval);

        return (
          <Box
            key={`history-skeleton-header-day-${String(index)}`}
            sx={{
              position: 'relative',
              width: '100%',
              minWidth: 0,
              height: '16px',
              overflow: 'visible',
            }}
          >
            {visibleDay ? (
              <Typography
                sx={[
                  HEADER_SUBTEXT_SX,
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  },
                ]}
              >
                {formatHistoryHeaderDay(visibleDay.day)}
              </Typography>
            ) : showFallbackSpacer ? (
              <Typography
                sx={[
                  HEADER_SUBTEXT_SX,
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  },
                ]}
              >
                &nbsp;
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

const EntityDailyHistorySkeletonBodySection = ({
  blockSize,
  dayCount,
  nameColumnWidth,
  rowCount,
  rowHeight,
}: HistorySkeletonBodySectionProps): ReactElement => (
  <TableBody>
    {Array.from({ length: rowCount }, (_unused, rowIndex) => (
      <EntityDailyHistorySkeletonRow
        key={`history-skeleton-row-${String(rowIndex)}`}
        blockSize={blockSize}
        dayCount={dayCount}
        nameColumnWidth={nameColumnWidth}
        rowHeight={rowHeight}
        rowIndex={rowIndex}
      />
    ))}
  </TableBody>
);

const EntityDailyHistorySkeletonRow = ({
  blockSize,
  dayCount,
  nameColumnWidth,
  rowHeight,
  rowIndex,
}: HistorySkeletonColumnProps & {
  blockSize: number;
  dayCount: number;
  rowHeight: number;
  rowIndex: number;
}): ReactElement => (
  <TableRow
    sx={{
      backgroundColor: 'background.paper',
      height: `${rowHeight}px`,
    }}
  >
    <TableCell sx={historySkeletonBodyNameCellSx(nameColumnWidth)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('sm'),
          minHeight: `${rowHeight}px`,
          minWidth: 0,
        }}
      >
        <Skeleton
          variant="rounded"
          width={ROW_NAME_WIDTHS[rowIndex % ROW_NAME_WIDTHS.length]}
          sx={{
            height: `${HISTORY_ROW_NAME_SKELETON_HEIGHT}px`,
            transform: 'none',
            borderRadius: HISTORY_SKELETON_RADIUS,
            flexShrink: 0,
          }}
        />
      </Box>
    </TableCell>
    <TableCell
      sx={[
        {
          backgroundColor: 'background.paper',
          px: s('xl'),
          py: s('none'),
          verticalAlign: 'middle',
          borderBottom: 'none',
          width: '100%',
        },
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: `${rowHeight}px` }}>
        <EntityDailyHistorySkeletonStatusCells dayCount={dayCount} blockSize={blockSize} />
      </Box>
    </TableCell>
  </TableRow>
);

const EntityDailyHistorySkeletonStatusCells = ({
  blockSize,
  dayCount,
}: {
  blockSize: number;
  dayCount: number;
}): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
      columnGap: s('md'),
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
    }}
  >
    {Array.from({ length: dayCount }, (_dayValue, dayIndex) => (
      <Skeleton
        key={`history-skeleton-cell-${String(dayIndex)}`}
        variant="rounded"
        sx={{
          width: '100%',
          minWidth: `${blockSize}px`,
          height: `${HISTORY_STATUS_BLOCK_SKELETON_HEIGHT}px`,
          transform: 'none',
          borderRadius: r('sm'),
        }}
      />
    ))}
  </Box>
);

export const EntityDailyHistoryPanelSkeleton = ({
  dayCount,
  days = [],
  entityLabel = 'Entity',
  entityHeaderContent,
  sortDirection,
  rowCount,
  nameColumnWidth,
  rowHeight,
  blockSize,
  headerHeight,
  visibleDayHeaderIndices,
  dayHeaderGridRef,
  nameColumnHeaderCellRef,
  onNameColumnResizeStart,
  onNameColumnResizeKeyDown,
}: EntityDailyHistoryPanelSkeletonProps): ReactElement => {
  const visibleDayHeaderLabels = buildVisibleDayHeaderLabels(days, visibleDayHeaderIndices);
  const visibleDayHeaderLabelsByIndex = new Map(
    visibleDayHeaderLabels.map((entry) => [entry.index, entry.day] as const),
  );

  return (
    <Box>
      <Table aria-label="Loading history" sx={historySkeletonTableSx(nameColumnWidth)}>
        <EntityDailyHistorySkeletonColumns nameColumnWidth={nameColumnWidth} />
        <EntityDailyHistorySkeletonHeaderSection
          dayCount={dayCount}
          entityLabel={entityLabel}
          entityHeaderContent={entityHeaderContent}
          sortDirection={sortDirection}
          headerHeight={headerHeight}
          dayHeaderGridRef={dayHeaderGridRef}
          nameColumnWidth={nameColumnWidth}
          nameColumnHeaderCellRef={nameColumnHeaderCellRef}
          onNameColumnResizeStart={onNameColumnResizeStart}
          onNameColumnResizeKeyDown={onNameColumnResizeKeyDown}
          visibleDayHeaderLabelsByIndex={visibleDayHeaderLabelsByIndex}
        />
        <EntityDailyHistorySkeletonBodySection
          blockSize={blockSize}
          dayCount={dayCount}
          nameColumnWidth={nameColumnWidth}
          rowCount={rowCount}
          rowHeight={rowHeight}
        />
      </Table>
    </Box>
  );
};
